import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import Modal, {
  ModalPrimaryButton,
  ModalSecondaryButton,
} from "../common/Modal";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useDefaultProfessionFilter } from "../../hooks/useDefaultProfessionFilter";
import classService from "../../services/classService";
import schoolService from "../../services/schoolService";
import { getProfessionColors } from "../../utils/professionColors";
import { PROFESSIONAL_FIELDS } from "../../utils/constants";
import {
  formatTrack,
  getTrackBadgeClasses,
  getClassStatus,
  getClassStatusPresentation,
  isArchivedClass,
  CLASS_STATUS,
} from "../../utils/classDisplay";

// classService rethrows the parsed response body, which for Spring's default
// error shape has no `message` - fall back to the status/error fields instead
// of rendering "[object Object]".
const describeApiError = (err) => {
  if (!err) return "Unknown error";
  if (typeof err === "string") return err;
  if (err.message) return err.message;
  if (err.error) return `${err.error}${err.status ? ` (${err.status})` : ""}`;
  return "Unknown error";
};

// Apprenticeship duration in years: Informatik and Mediamatik run 4 years,
// KV, Detailhandel and Kundendialog 3.
const getApprenticeshipYears = (professionType) => {
  switch (professionType) {
    case "INFORMATIK":
    case "MEDIAMATIK":
      return 4;
    case "KUNDENDIALOG":
    case "DETAILHANDEL":
    case "KV":
      return 3;
    default:
      return null;
  }
};

const getAutoCreatedSemesterCount = (professionType) => {
  const years = getApprenticeshipYears(professionType);
  return years ? years * 2 : null;
};

// Swiss/German date handling. The form keeps ISO strings for the API, but the
// inputs are plain text so they always read dd.mm.yyyy - a native date input
// renders in the browser's locale, which shows mm/dd/yyyy on en-US machines.
const toSwissDate = (iso) => {
  if (!iso) return "";
  const [year, month, day] = String(iso).slice(0, 10).split("-");
  if (!year || !month || !day) return "";
  return `${day}.${month}.${year}`;
};

const parseSwissDate = (text) => {
  const match = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec((text || "").trim());
  if (!match) return "";

  const [, day, month, year] = match;
  const iso = `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  const date = new Date(`${iso}T00:00:00`);
  // Rejects things like 31.02.2026, which Date would silently roll over.
  if (Number.isNaN(date.getTime()) || date.getDate() !== Number(day)) return "";
  return iso;
};

// Apprenticeships start on 1 August and end on 31 July, `years` later.
const defaultStartDate = () => `${new Date().getFullYear()}-08-01`;

const endDateFor = (startIso, professionType) => {
  const years = getApprenticeshipYears(professionType);
  if (!startIso || !years) return "";
  const startYear = Number(String(startIso).slice(0, 4));
  if (!startYear) return "";
  return `${startYear + years}-07-31`;
};

const normalizeProfessionType = (professionType) => {
  const value = (professionType || "").toUpperCase();

  switch (value) {
    case "INFORMATIKER":
      return "INFORMATIK";
    case "MEDIAMATIKER":
      return "MEDIAMATIK";
    default:
      return value;
  }
};

const formatProfession = (profession) => {
  switch (normalizeProfessionType(profession)) {
    case "INFORMATIK":
      return "IT";
    case "MEDIAMATIK":
      return "MED";
    case "KUNDENDIALOG":
      return "KD";
    case "DETAILHANDEL":
      return "DH";
    case "KV":
      return "KV";
    default:
      return profession;
  }
};

const professionLabel = (profession) =>
  PROFESSIONAL_FIELDS.find((f) => f.id === normalizeProfessionType(profession))
    ?.label || profession;

const getProfessionBadgeClasses = (profession) => {
  const colors = getProfessionColors(normalizeProfessionType(profession));
  return `bg-${colors.light} text-${colors.text} border border-${colors.border}`;
};

const emptyClassForm = (professionType, schoolId) => {
  const profession = normalizeProfessionType(professionType) || "";
  const startDate = defaultStartDate();

  return {
    name: "",
    description: "",
    professionType: profession,
    // The track is a deliberate choice, so it starts empty.
    track: "",
    startDate,
    endDate: endDateFor(startDate, profession),
    schoolId: schoolId ? String(schoolId) : "",
    isActive: true,
  };
};

const classSchoolId = (classItem) =>
  classItem.schoolId ?? classItem.school?.id ?? null;

const SchoolClassManagement = () => {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();

  const [schools, setSchools] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Filters. The profession defaults to the signed-in user's own profession, so
  // an admin/Berufsbildner lands on their own schools without touching a filter.
  const [professionFilter, setProfessionFilter] = useDefaultProfessionFilter(
    currentUser,
    "professionType",
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [trackFilter, setTrackFilter] = useState("ALL");
  const [collapsedSchools, setCollapsedSchools] = useState(() => new Set());
  const [archiveOpen, setArchiveOpen] = useState(false);

  // School modals
  const [showSchoolModal, setShowSchoolModal] = useState(false);
  const [editingSchool, setEditingSchool] = useState(null);
  const [schoolForm, setSchoolForm] = useState({
    name: "",
    description: "",
    professionType: "",
  });
  const [schoolToDelete, setSchoolToDelete] = useState(null);

  // Class modals
  const [showClassModal, setShowClassModal] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [classForm, setClassForm] = useState(() => emptyClassForm("", ""));
  // What the two date fields currently show, so a half-typed date survives a
  // keystroke. `classForm` keeps the ISO value the API wants.
  const [classDateText, setClassDateText] = useState({
    startDate: "",
    endDate: "",
  });
  const [classToDelete, setClassToDelete] = useState(null);
  const [deleteImpact, setDeleteImpact] = useState(null);
  const [deleteImpactLoading, setDeleteImpactLoading] = useState(false);

  const canEdit = Boolean(
    currentUser?.roles?.some((role) =>
      ["ADMIN", "BERUFSBILDNER"].includes(role.name),
    ),
  );

  const activeProfession = normalizeProfessionType(professionFilter);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const professionParam =
        activeProfession && activeProfession !== "ALL"
          ? activeProfession
          : null;

      const [schoolsResponse, classesResponse] = await Promise.all([
        schoolService.getAllSchools(professionParam),
        // Every class is grouped under its school here, so all of them are
        // needed - the endpoint otherwise returns the first 20 by name. No
        // `active` filter either: switched-off classes are shown with their
        // status rather than hidden.
        classService.getClasses({ size: 1000 }),
      ]);

      setSchools(Array.isArray(schoolsResponse) ? schoolsResponse : []);

      const loadedClasses = Array.isArray(classesResponse?.content)
        ? classesResponse.content
        : Array.isArray(classesResponse)
          ? classesResponse
          : [];
      setClasses(loadedClasses);
    } catch (err) {
      console.error("Error loading schools and classes:", err);
      setError("Error loading data: " + describeApiError(err));
      setSchools([]);
      setClasses([]);
    } finally {
      setLoading(false);
    }
  }, [activeProfession]);

  useEffect(() => {
    if (currentUser && canEdit) {
      loadData();
    }
  }, [currentUser, canEdit, loadData]);

  // Group the classes under their school, applying the search/track filters.
  const groups = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();
    const hasClassFilter = Boolean(search) || trackFilter !== "ALL";

    const matchesFilters = (classItem) => {
      if (
        activeProfession &&
        activeProfession !== "ALL" &&
        normalizeProfessionType(classItem.professionType) !== activeProfession
      ) {
        return false;
      }
      if (trackFilter !== "ALL" && classItem.track !== trackFilter)
        return false;
      if (search) {
        const haystack =
          `${classItem.name || ""} ${classItem.description || ""}`.toLowerCase();
        if (!haystack.includes(search)) return false;
      }
      return true;
    };

    // Classes whose end date has passed move out of the school blocks and into
    // the archive at the bottom of the page.
    const visibleClasses = classes
      .filter(matchesFilters)
      .filter((classItem) => !isArchivedClass(classItem));

    const schoolGroups = schools.map((school) => ({
      school,
      classes: visibleClasses.filter(
        (classItem) => String(classSchoolId(classItem)) === String(school.id),
      ),
    }));

    const knownSchoolIds = new Set(schools.map((s) => String(s.id)));
    const orphanClasses = visibleClasses.filter(
      (classItem) => !knownSchoolIds.has(String(classSchoolId(classItem))),
    );

    // While a class filter is active, empty schools are just noise.
    const shown = hasClassFilter
      ? schoolGroups.filter((group) => group.classes.length > 0)
      : schoolGroups;

    if (orphanClasses.length > 0) {
      shown.push({ school: null, classes: orphanClasses });
    }

    return shown;
  }, [schools, classes, searchTerm, trackFilter, activeProfession]);

  const totalVisibleClasses = groups.reduce(
    (sum, group) => sum + group.classes.length,
    0,
  );

  const archivedClasses = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();

    return classes.filter((classItem) => {
      if (!isArchivedClass(classItem)) return false;
      if (
        activeProfession &&
        activeProfession !== "ALL" &&
        normalizeProfessionType(classItem.professionType) !== activeProfession
      ) {
        return false;
      }
      if (trackFilter !== "ALL" && classItem.track !== trackFilter)
        return false;
      if (search && !(classItem.name || "").toLowerCase().includes(search))
        return false;
      return true;
    });
  }, [classes, searchTerm, trackFilter, activeProfession]);

  const toggleSchool = (schoolId) => {
    setCollapsedSchools((prev) => {
      const next = new Set(prev);
      const key = String(schoolId);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // --- School CRUD -------------------------------------------------------

  const openCreateSchool = () => {
    setEditingSchool(null);
    setSchoolForm({
      name: "",
      description: "",
      professionType: activeProfession !== "ALL" ? activeProfession : "",
    });
    setError(null);
    setShowSchoolModal(true);
  };

  const openEditSchool = (school) => {
    setEditingSchool(school);
    setSchoolForm({
      name: school.name || "",
      description: school.description || "",
      professionType: normalizeProfessionType(school.professionType) || "",
    });
    setError(null);
    setShowSchoolModal(true);
  };

  const handleSaveSchool = async () => {
    if (!schoolForm.name?.trim()) {
      setError("School name is required");
      return;
    }
    if (!schoolForm.professionType) {
      setError("Profession type is required");
      return;
    }

    try {
      setError(null);
      if (editingSchool) {
        await schoolService.updateSchool(editingSchool.id, schoolForm);
        setSuccess("School updated successfully");
      } else {
        await schoolService.createSchool(schoolForm);
        setSuccess("School created successfully");
      }
      setShowSchoolModal(false);
      setEditingSchool(null);
      await loadData();
    } catch (err) {
      setError("Error saving school: " + describeApiError(err));
    }
  };

  // Every class of the school being deleted, archived ones included - the
  // school cannot go while any of them still points at it.
  const schoolClassesToDelete = schoolToDelete
    ? classes.filter(
        (classItem) =>
          String(classSchoolId(classItem)) === String(schoolToDelete.id),
      )
    : [];

  const confirmDeleteSchool = async () => {
    if (!schoolToDelete) return;
    try {
      await schoolService.deleteSchool(schoolToDelete.id);
      setSuccess("School deleted successfully");
      setSchoolToDelete(null);
      await loadData();
    } catch (err) {
      setError("Error deleting school: " + describeApiError(err));
      setSchoolToDelete(null);
    }
  };

  // --- Class CRUD --------------------------------------------------------

  const showClassForm = (form) => {
    setClassForm(form);
    setClassDateText({
      startDate: toSwissDate(form.startDate),
      endDate: toSwissDate(form.endDate),
    });
    setError(null);
    setShowClassModal(true);
  };

  const openCreateClass = (school) => {
    setEditingClass(null);
    showClassForm(
      emptyClassForm(
        school?.professionType ||
          (activeProfession !== "ALL" ? activeProfession : ""),
        school?.id,
      ),
    );
  };

  const openEditClass = (classItem) => {
    setEditingClass(classItem);
    showClassForm({
      name: classItem.name || "",
      description: classItem.description || "",
      professionType: normalizeProfessionType(classItem.professionType) || "",
      track: classItem.track || "",
      startDate: classItem.startDate || "",
      endDate: classItem.endDate || "",
      schoolId: String(classSchoolId(classItem) ?? ""),
      isActive: classItem.isActive !== undefined ? classItem.isActive : true,
    });
  };

  const handleClassInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Picking a profession on a new class re-derives the end date from the
    // apprenticeship duration. On an existing class the dates are left alone.
    if (name === "professionType" && !editingClass) {
      const endDate = endDateFor(classForm.startDate, value);
      setClassForm((prev) => ({ ...prev, professionType: value, endDate }));
      setClassDateText((prev) => ({ ...prev, endDate: toSwissDate(endDate) }));
      return;
    }

    setClassForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleClassDateChange = (field, text) => {
    const iso = parseSwissDate(text);
    setClassDateText((prev) => ({ ...prev, [field]: text }));
    setClassForm((prev) => ({ ...prev, [field]: iso }));

    // Moving the start of a new class moves its end with it.
    if (field === "startDate" && iso && !editingClass) {
      const endDate = endDateFor(iso, classForm.professionType);
      if (endDate) {
        setClassForm((prev) => ({ ...prev, startDate: iso, endDate }));
        setClassDateText((prev) => ({
          ...prev,
          startDate: text,
          endDate: toSwissDate(endDate),
        }));
      }
    }
  };

  // Returns an error message, or null when the form can be submitted.
  const validateClassForm = () => {
    if (!classForm.name?.trim()) return "Please enter a class name";
    if (!classForm.schoolId) return "Please select a school";
    if (!classForm.professionType) return "Please select a profession";
    if (!classForm.track) return "Please select a type (Vocational or BMS)";
    if (classDateText.startDate.trim() && !classForm.startDate)
      return "Please enter the start date as dd.mm.yyyy";
    if (classDateText.endDate.trim() && !classForm.endDate)
      return "Please enter the end date as dd.mm.yyyy";
    if (
      classForm.startDate &&
      classForm.endDate &&
      classForm.endDate < classForm.startDate
    )
      return "The end date must be after the start date";
    return null;
  };

  const handleSaveClass = async (e) => {
    if (e) e.preventDefault();

    // The submit button sits outside the <form>, so the `required` attributes
    // on the inputs never fire - the form has to be checked here.
    const validationError = validateClassForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const payload = {
        ...classForm,
        startDate: classForm.startDate || null,
        endDate: classForm.endDate || null,
      };

      if (editingClass) {
        await classService.updateClass(editingClass.id, payload);
        setSuccess("Class updated successfully");
      } else {
        await classService.createClass(payload);
        setSuccess("Class created successfully");
      }
      setShowClassModal(false);
      setEditingClass(null);
      await loadData();
    } catch (err) {
      setError(
        `Error ${editingClass ? "updating" : "creating"} class: ` +
          describeApiError(err),
      );
    } finally {
      setLoading(false);
    }
  };

  const openDeleteClass = async (classItem) => {
    setClassToDelete(classItem);
    setDeleteImpact(null);
    setDeleteImpactLoading(true);
    try {
      setDeleteImpact(await classService.getDeleteImpact(classItem.id));
    } catch (err) {
      setError("Error loading delete preview: " + describeApiError(err));
    } finally {
      setDeleteImpactLoading(false);
    }
  };

  const handleDeleteClass = async () => {
    try {
      setLoading(true);
      await classService.deleteClass(classToDelete.id);
      setSuccess("Class deleted successfully");
      setClassToDelete(null);
      setDeleteImpact(null);
      await loadData();
    } catch (err) {
      setError("Error deleting class: " + describeApiError(err));
    } finally {
      setLoading(false);
    }
  };

  // --- Render ------------------------------------------------------------

  if (!currentUser) {
    return (
      <div className="min-h-screen bg-ice-white p-6">
        <div className="max-w-md mx-auto mt-20">
          <div className="card-brand">
            <div className="flex items-center mb-4">
              <i className="fas fa-exclamation-triangle text-warm-sunset text-2xl mr-3"></i>
              <h2 className="text-xl font-bold text-grey-dusk">
                Authentication Required
              </h2>
            </div>
            <p className="text-slate-cloud mb-6">
              You need to be logged in to access Schools &amp; Classes.
            </p>
            <button
              className="btn-brand-primary w-full"
              onClick={() => (window.location.href = "/login")}
            >
              <i className="fas fa-sign-in-alt mr-2"></i>
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!canEdit) {
    return (
      <div className="min-h-screen bg-ice-white flex items-center justify-center p-4">
        <div className="card-brand p-8 max-w-lg w-full text-center">
          <div className="w-20 h-20 bg-sunrise-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="fas fa-exclamation-triangle text-sunrise-red text-2xl"></i>
          </div>
          <h2 className="text-2xl font-bold text-grey-dusk mb-4">
            Access Denied
          </h2>
          <p className="text-slate-cloud">
            You need ADMIN or BERUFSBILDNER role to manage schools and classes.
          </p>
        </div>
      </div>
    );
  }

  const renderClassCard = (classItem) => {
    const status = getClassStatus(classItem);
    const statusPresentation = getClassStatusPresentation(status);
    const school = schools.find(
      (s) => String(s.id) === String(classSchoolId(classItem)),
    );

    return (
      <div
        key={classItem.id}
        className="card-brand p-4 group hover:scale-[1.01] transition-all duration-300"
      >
        {/* Name first, badges on their own row - three of them never fit
            beside the name in a card this narrow. */}
        <div className="mb-4">
          <h3
            className="truncate text-xl font-bold leading-tight text-grey-dusk group-hover:text-sunrise-red transition-colors duration-300"
            title={classItem.name}
          >
            {classItem.name}
          </h3>
          {classItem.description && (
            <p className="mt-1.5 line-clamp-2 text-xs text-gray-600">
              {classItem.description}
            </p>
          )}
          <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
            <span
              className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getProfessionBadgeClasses(classItem.professionType)}`}
            >
              {formatProfession(classItem.professionType)}
            </span>
            <span
              className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getTrackBadgeClasses(classItem.track)}`}
            >
              {formatTrack(classItem.track)}
            </span>
            {/* A running class needs no badge - that it is listed here at all
                already says so. Only the exceptions are worth the space. */}
            {status !== CLASS_STATUS.ACTIVE && (
              <span
                className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${statusPresentation.classes}`}
                title={statusPresentation.hint}
              >
                <i className={`fas ${statusPresentation.icon} mr-1.5`}></i>
                {statusPresentation.label}
              </span>
            )}
          </div>
        </div>

        <div className="mb-4 space-y-2.5">
          {status === CLASS_STATUS.ARCHIVED && (
            <div className="flex items-center text-gray-600 text-sm">
              <i className="fas fa-school w-4 mr-2.5 text-sunrise-red"></i>
              <span className="font-medium">
                {school ? school.name : "No school"}
              </span>
            </div>
          )}
          <div className="flex items-center text-gray-600 text-sm">
            <i className="fas fa-calendar w-4 mr-2.5 text-sunrise-red"></i>
            <span>
              {classItem.startDate && classItem.endDate
                ? `${toSwissDate(classItem.startDate)} - ${toSwissDate(classItem.endDate)}`
                : classItem.startDate
                  ? toSwissDate(classItem.startDate)
                  : "No dates set"}
            </span>
          </div>
          <div className="flex items-center text-gray-600 text-sm">
            <i className="fas fa-users w-4 mr-2.5 text-sunrise-red"></i>
            <span className="bg-pale-dawn px-3 py-1 rounded-full text-xs font-medium text-grey-dusk">
              {classItem.memberCount || 0} Students
            </span>
          </div>
        </div>

        <div className="flex space-x-2 border-t border-gray-200 pt-3.5">
          <button
            onClick={() =>
              navigate(`/manage/classes/${classItem.id}`, {
                state: { from: "management-hub" },
              })
            }
            className="flex-1 bg-ice-white/10 hover:bg-ice-white/20 text-green-cloud border border-green-cloud/30 hover:border-green-cloud/50 px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center justify-center space-x-2"
            title="View Details & Manage Students"
          >
            <i className="fas fa-users"></i>
            <span>Manage</span>
          </button>
          <button
            onClick={() => openEditClass(classItem)}
            className="bg-slate-cloud/10 hover:bg-slate-cloud/20 text-slate-cloud border border-slate-cloud/30 hover:border-slate-cloud/50 px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center justify-center"
            title="Edit Class"
          >
            <i className="fas fa-edit"></i>
          </button>
          <button
            onClick={() => openDeleteClass(classItem)}
            className="bg-sunrise-red/10 hover:bg-sunrise-red/20 text-sunrise-red border border-sunrise-red/30 hover:border-sunrise-red/50 px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center justify-center"
            title="Delete Class"
          >
            <i className="fas fa-trash"></i>
          </button>
        </div>
      </div>
    );
  };

  const autoCreatedSemesterCountForClass = getAutoCreatedSemesterCount(
    classForm.professionType,
  );
  const autoCreatedSemesterCountForSchool = getAutoCreatedSemesterCount(
    schoolForm.professionType,
  );

  const renderGroups = () => (
    <div className="space-y-6">
      {groups.map(({ school, classes: groupClasses }) => {
        const key = school ? String(school.id) : "unassigned";
        const collapsed = collapsedSchools.has(key);

        return (
          <div
            key={key}
            className="rounded-2xl border border-pale-dawn bg-white shadow-sm overflow-hidden"
          >
            {/* School header */}
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-pale-dawn bg-pale-dawn/30 px-5 py-4">
              <button
                type="button"
                onClick={() => toggleSchool(key)}
                className="flex min-w-0 items-center gap-3 text-left"
                aria-expanded={!collapsed}
              >
                <i
                  className={`fas ${collapsed ? "fa-chevron-right" : "fa-chevron-down"} text-slate-cloud text-sm w-3`}
                ></i>
                <i
                  className={`fas ${school ? "fa-school" : "fa-question-circle"} text-sunrise-red text-lg`}
                ></i>
                <span className="truncate text-xl font-bold text-grey-dusk">
                  {school ? school.name : "Classes without a school"}
                </span>
                {school?.professionType && (
                  <span
                    className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getProfessionBadgeClasses(school.professionType)}`}
                  >
                    {professionLabel(school.professionType)}
                  </span>
                )}
                <span className="rounded-full bg-white px-2.5 py-0.5 text-xs font-medium text-slate-cloud border border-pale-dawn">
                  {groupClasses.length}{" "}
                  {groupClasses.length === 1 ? "class" : "classes"}
                </span>
              </button>

              {school && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="btn-brand-primary py-1.5 px-3 text-sm"
                    onClick={() => openCreateClass(school)}
                    title={`Add a class to ${school.name}`}
                  >
                    <i className="fas fa-plus mr-2"></i>
                    New Class
                  </button>
                  <button
                    type="button"
                    className="p-2 text-slate-cloud hover:text-sunrise-blue hover:bg-white rounded-lg transition-colors"
                    onClick={() => openEditSchool(school)}
                    title="Edit School"
                  >
                    <i className="fas fa-edit"></i>
                  </button>
                  <button
                    type="button"
                    className="p-2 text-sunrise-red hover:bg-white rounded-lg transition-colors"
                    onClick={() => setSchoolToDelete(school)}
                    title="Delete School"
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                </div>
              )}
            </div>

            {/* Classes */}
            {!collapsed && (
              <div className="p-5">
                {groupClasses.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-pale-dawn bg-ice-white px-6 py-8 text-center">
                    <p className="text-slate-cloud mb-4">
                      No classes in this school yet.
                    </p>
                    {school && (
                      <button
                        type="button"
                        className="btn-brand-outline"
                        onClick={() => openCreateClass(school)}
                      >
                        <i className="fas fa-plus mr-2"></i>
                        Create the first class
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {groupClasses.map(renderClassCard)}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  const renderArchive = () => (
    <div className="mt-6 rounded-2xl border border-gray-300 bg-gray-50 overflow-hidden">
      <button
        type="button"
        onClick={() => setArchiveOpen((open) => !open)}
        className="flex w-full items-center gap-3 px-5 py-4 text-left"
        aria-expanded={archiveOpen}
      >
        <i
          className={`fas ${archiveOpen ? "fa-chevron-down" : "fa-chevron-right"} text-slate-cloud text-sm w-3`}
        ></i>
        <i className="fas fa-box-archive text-slate-cloud text-lg"></i>
        <span className="text-lg font-bold text-grey-dusk">Archive</span>
        <span className="rounded-full bg-white px-2.5 py-0.5 text-xs font-medium text-slate-cloud border border-gray-300">
          {archivedClasses.length}{" "}
          {archivedClasses.length === 1 ? "class" : "classes"}
        </span>
        <span className="ml-auto text-xs text-slate-cloud">
          Classes whose end date has passed
        </span>
      </button>

      {archiveOpen && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 border-t border-gray-300 p-5">
          {archivedClasses.map(renderClassCard)}
        </div>
      )}
    </div>
  );

  return (
    <>
      <div className="bg-ice-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <div className="card-brand-elevated p-6 bg-gradient-to-r from-ice-white to-pale-dawn/30">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-grey-dusk flex items-center mb-2">
                    <i className="fas fa-school text-sunrise-red mr-4"></i>
                    Schools &amp; Classes
                  </h1>
                  <p className="text-slate-cloud text-lg">
                    Manage schools and the classes that belong to them
                  </p>
                </div>
                <div className="mt-4 lg:mt-0">
                  <button
                    className="btn-brand-primary"
                    onClick={openCreateSchool}
                    disabled={loading}
                  >
                    <i className="fas fa-plus mr-2"></i>
                    New School
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="card-brand">
            <div className="p-6">
              {/* While a modal is open it renders the error itself - showing it
                  behind the overlay as well would just duplicate it. */}
              {error && !showSchoolModal && !showClassModal && (
                <div className="bg-sunrise-50 border border-sunrise-200 text-sunrise-700 px-6 py-4 rounded-lg mb-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <i className="fas fa-exclamation-triangle text-sunrise-red mr-3"></i>
                      {error}
                    </div>
                    <button
                      onClick={() => setError(null)}
                      className="text-sunrise-red hover:text-sunrise-600"
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                </div>
              )}
              {success && (
                <div className="alert-brand-success">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <i className="fas fa-check-circle text-green-cloud mr-3"></i>
                      {success}
                    </div>
                    <button
                      onClick={() => setSuccess(null)}
                      className="text-green-cloud hover:text-green-cloud/80"
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                </div>
              )}

              {/* Filters */}
              <div className="mb-5 rounded-xl border border-pale-dawn bg-white px-4 py-3 shadow-sm">
                <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
                  <div className="relative w-full xl:max-w-sm">
                    <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs"></i>
                    <input
                      type="text"
                      className="input-brand h-9 w-full rounded-lg bg-ice-white pl-8 pr-3 text-sm shadow-none"
                      placeholder="Search class..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>

                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] font-bold uppercase tracking-wide text-slate-cloud">
                        Track
                      </span>
                      <div className="flex rounded-lg border border-pale-dawn bg-ice-white p-1">
                        {["ALL", "BMS", "VOCATIONAL"].map((track) => (
                          <button
                            key={track}
                            type="button"
                            onClick={() => setTrackFilter(track)}
                            className={`h-7 px-3 rounded-md text-xs font-semibold transition-colors ${
                              trackFilter === track
                                ? "bg-sunrise-red text-white shadow-sm"
                                : "text-slate-cloud hover:bg-white hover:text-grey-dusk"
                            }`}
                          >
                            {track === "ALL" ? "All" : formatTrack(track)}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="text-[11px] font-bold uppercase tracking-wide text-slate-cloud">
                        Profession
                      </span>
                      <div className="flex flex-wrap rounded-lg border border-pale-dawn bg-ice-white p-1">
                        {[
                          "ALL",
                          "INFORMATIK",
                          "MEDIAMATIK",
                          "KV",
                          "KUNDENDIALOG",
                          "DETAILHANDEL",
                        ].map((profession) => (
                          <button
                            key={profession}
                            type="button"
                            onClick={() => setProfessionFilter(profession)}
                            className={`h-7 px-3 rounded-md text-xs font-semibold transition-colors ${
                              activeProfession === profession
                                ? "bg-sunrise-red text-white shadow-sm"
                                : "text-slate-cloud hover:bg-white hover:text-grey-dusk"
                            }`}
                          >
                            {profession === "ALL"
                              ? "All"
                              : formatProfession(profession)}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* School groups */}
              {loading && schools.length === 0 ? (
                <div className="text-center py-16">
                  <div className="w-16 h-16 bg-sunrise-red/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="fas fa-spinner fa-spin text-sunrise-red text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-grey-dusk mb-2">
                    Loading schools and classes
                  </h3>
                  <p className="text-slate-cloud">Please wait...</p>
                </div>
              ) : schools.length === 0 ? (
                <div className="text-center py-16">
                  <div className="w-32 h-32 bg-sunrise-red/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className="fas fa-school text-sunrise-red text-4xl"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-grey-dusk mb-4">
                    No Schools Available
                  </h3>
                  <p className="text-slate-cloud mb-8">
                    Create your first school to get started - classes are added
                    inside a school.
                  </p>
                  <button
                    className="btn-brand-primary"
                    onClick={openCreateSchool}
                  >
                    <i className="fas fa-plus mr-2"></i>
                    Create First School
                  </button>
                </div>
              ) : groups.length === 0 || totalVisibleClasses === 0 ? (
                searchTerm.trim() || trackFilter !== "ALL" ? (
                  <div className="text-center py-16">
                    <div className="w-24 h-24 bg-sunrise-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <i className="fas fa-search text-sunrise-red text-3xl"></i>
                    </div>
                    <h3 className="text-2xl font-bold text-grey-dusk mb-2">
                      No classes found
                    </h3>
                    <p className="text-slate-cloud">
                      No class matches the current search and filters.
                    </p>
                  </div>
                ) : (
                  renderGroups()
                )
              ) : (
                renderGroups()
              )}

              {schools.length > 0 &&
                archivedClasses.length > 0 &&
                renderArchive()}
            </div>
          </div>
        </div>
      </div>

      {/* School Modal */}
      {showSchoolModal &&
        createPortal(
          <Modal
            isOpen
            onClose={() => setShowSchoolModal(false)}
            title={editingSchool ? "Edit school" : "Create new school"}
            icon="fa-school"
            size="lg"
            footer={
              <>
                <ModalSecondaryButton
                  onClick={() => setShowSchoolModal(false)}
                >
                  Cancel
                </ModalSecondaryButton>
                <ModalPrimaryButton onClick={handleSaveSchool}>
                  <i className="fas fa-save"></i>
                  {editingSchool ? "Save changes" : "Create school"}
                </ModalPrimaryButton>
              </>
            }
          >
              <div>
                <div className="mb-6">
                  <label
                    htmlFor="schoolName"
                    className="block text-sm font-medium text-grey-dusk mb-2"
                  >
                    <i className="fas fa-school text-sunrise-red mr-2"></i>
                    School Name *
                  </label>
                  <input
                    type="text"
                    className="input-brand text-lg"
                    id="schoolName"
                    value={schoolForm.name}
                    onChange={(e) =>
                      setSchoolForm({ ...schoolForm, name: e.target.value })
                    }
                    placeholder="Enter school name"
                    autoFocus
                  />
                </div>

                <div className="mb-6">
                  <label
                    htmlFor="schoolProfessionType"
                    className="block text-sm font-medium text-grey-dusk mb-2"
                  >
                    <i className="fas fa-id-badge text-sunrise-red mr-2"></i>
                    Profession Type *
                  </label>
                  <select
                    id="schoolProfessionType"
                    className="input-brand w-full"
                    value={schoolForm.professionType || ""}
                    onChange={(e) =>
                      setSchoolForm({
                        ...schoolForm,
                        professionType: e.target.value,
                      })
                    }
                    required
                  >
                    <option value="" disabled>
                      Select Profession...
                    </option>
                    {PROFESSIONAL_FIELDS.map((field) => (
                      <option key={field.id} value={field.id}>
                        {field.label}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-slate-cloud mt-1 ml-6">
                    Pre-filled from your own profession - change it if needed.
                  </p>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start">
                    <i className="fas fa-info-circle text-sunrise-red mr-3 mt-1"></i>
                    <div>
                      <p className="font-semibold text-grey-dusk">
                        Auto-creation:
                      </p>
                      <p className="text-sm text-gray-600">
                        {autoCreatedSemesterCountForSchool
                          ? `${autoCreatedSemesterCountForSchool} semesters (Semester 1-${autoCreatedSemesterCountForSchool}) will be automatically created for classes in this school.`
                          : "The semester count for classes in this school will be determined by the selected profession type."}
                      </p>
                    </div>
                  </div>
                </div>

                {error && (
                  <div className="bg-sunrise-red/10 border border-sunrise-red/30 text-sunrise-red px-4 py-3 rounded-lg flex items-center">
                    <i className="fas fa-exclamation-triangle mr-2"></i>
                    {error}
                  </div>
                )}
              </div>
          </Modal>,
          document.body,
        )}

      {/* Class Modal */}
      {showClassModal &&
        createPortal(
          <Modal
            isOpen
            onClose={() => {
              setShowClassModal(false);
              setEditingClass(null);
            }}
            title={editingClass ? "Edit class" : "Create new class"}
            subtitle={
              editingClass
                ? "Update class information"
                : schools.find(
                      (s) => String(s.id) === String(classForm.schoolId),
                    )?.name
                  ? `New class in ${
                      schools.find(
                        (s) => String(s.id) === String(classForm.schoolId),
                      ).name
                    }`
                  : "Add a new class"
            }
            icon={editingClass ? "fa-edit" : "fa-plus"}
            size="2xl"
            closeOnBackdrop={!loading}
            footer={
              <>
                <ModalSecondaryButton
                  onClick={() => {
                    setShowClassModal(false);
                    setEditingClass(null);
                  }}
                  disabled={loading}
                >
                  Cancel
                </ModalSecondaryButton>
                <ModalPrimaryButton
                  onClick={handleSaveClass}
                  loading={loading}
                  loadingText={editingClass ? "Saving..." : "Creating..."}
                >
                  <i className={`fas ${editingClass ? "fa-save" : "fa-plus"}`}></i>
                  {editingClass ? "Save changes" : "Create class"}
                </ModalPrimaryButton>
              </>
            }
          >
              <form
                onSubmit={handleSaveClass}
                className="p-6 overflow-y-auto flex-1 min-h-0"
              >
                <div className="space-y-6">
                  <div>
                    <label
                      htmlFor="class-name"
                      className="block text-sm font-semibold text-grey-dusk mb-2"
                    >
                      <i className="fas fa-tag mr-2 text-sunrise-red"></i>
                      Class Name <span className="text-sunrise-red">*</span>
                    </label>
                    <input
                      type="text"
                      id="class-name"
                      name="name"
                      value={classForm.name}
                      onChange={handleClassInputChange}
                      className="input-brand"
                      placeholder="Enter class name"
                      required
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="class-description"
                      className="block text-sm font-semibold text-grey-dusk mb-2"
                    >
                      <i className="fas fa-align-left mr-2 text-sunrise-red"></i>
                      Description
                    </label>
                    <textarea
                      id="class-description"
                      name="description"
                      value={classForm.description}
                      onChange={handleClassInputChange}
                      rows={3}
                      className="input-brand resize-none"
                      placeholder="Enter class description (optional)"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="class-schoolId"
                      className="block text-sm font-semibold text-grey-dusk mb-2"
                    >
                      <i className="fas fa-school mr-2 text-sunrise-red"></i>
                      School <span className="text-sunrise-red">*</span>
                    </label>
                    <select
                      id="class-schoolId"
                      name="schoolId"
                      value={classForm.schoolId}
                      onChange={handleClassInputChange}
                      className="input-brand"
                      required
                    >
                      <option value="">Select a school</option>
                      {schools.map((school) => (
                        <option key={school.id} value={school.id}>
                          {school.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label
                        htmlFor="class-professionType"
                        className="block text-sm font-semibold text-grey-dusk mb-2"
                      >
                        <i className="fas fa-briefcase mr-2 text-sunrise-red"></i>
                        Profession <span className="text-sunrise-red">*</span>
                      </label>
                      <select
                        id="class-professionType"
                        name="professionType"
                        value={classForm.professionType}
                        onChange={handleClassInputChange}
                        className="input-brand"
                        required
                      >
                        <option value="" disabled>
                          Select Profession...
                        </option>
                        {PROFESSIONAL_FIELDS.map((field) => (
                          <option key={field.id} value={field.id}>
                            {field.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label
                        htmlFor="class-track"
                        className="block text-sm font-semibold text-grey-dusk mb-2"
                      >
                        <i className="fas fa-route mr-2 text-sunrise-red"></i>
                        Type <span className="text-sunrise-red">*</span>
                      </label>
                      <select
                        id="class-track"
                        name="track"
                        value={classForm.track}
                        onChange={handleClassInputChange}
                        className="input-brand"
                        required
                      >
                        <option value="" disabled>
                          Select Type...
                        </option>
                        <option value="VOCATIONAL">Vocational</option>
                        <option value="BMS">BMS</option>
                      </select>
                    </div>
                  </div>

                  {!editingClass && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-start">
                        <i className="fas fa-info-circle text-sunrise-red mr-3 mt-1"></i>
                        <div>
                          <p className="font-semibold text-grey-dusk">
                            Auto-creation:
                          </p>
                          <p className="text-sm text-gray-600">
                            {autoCreatedSemesterCountForClass
                              ? `${autoCreatedSemesterCountForClass} semesters (Semester 1-${autoCreatedSemesterCountForClass}) will be created automatically for this class.`
                              : "The semester count will be determined by the selected profession type."}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label
                        htmlFor="class-startDate"
                        className="block text-sm font-semibold text-grey-dusk mb-2"
                      >
                        <i className="fas fa-calendar-plus mr-2 text-sunrise-red"></i>
                        Start Date
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        id="class-startDate"
                        name="startDate"
                        value={classDateText.startDate}
                        onChange={(e) =>
                          handleClassDateChange("startDate", e.target.value)
                        }
                        className="input-brand"
                        placeholder="dd.mm.yyyy"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="class-endDate"
                        className="block text-sm font-semibold text-grey-dusk mb-2"
                      >
                        <i className="fas fa-calendar-minus mr-2 text-sunrise-red"></i>
                        End Date
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        id="class-endDate"
                        name="endDate"
                        value={classDateText.endDate}
                        onChange={(e) =>
                          handleClassDateChange("endDate", e.target.value)
                        }
                        className="input-brand"
                        placeholder="dd.mm.yyyy"
                      />
                    </div>
                  </div>
                  {!editingClass && classForm.professionType && (
                    <p className="-mt-4 text-xs text-slate-cloud">
                      Pre-filled for a{" "}
                      {getApprenticeshipYears(classForm.professionType)}-year
                      apprenticeship (1 August to 31 July) - adjust if needed.
                    </p>
                  )}

                  <div className="rounded-lg border border-pale-dawn bg-ice-white p-4">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <p
                          className="text-sm font-semibold text-grey-dusk"
                          id="class-active-switch-label"
                        >
                          Active Class
                        </p>
                        <p className="text-xs text-slate-cloud mt-0.5">
                          {classForm.isActive
                            ? `Follows the dates: ${
                                getClassStatusPresentation(
                                  getClassStatus(classForm),
                                ).label
                              } right now. It archives itself once the end date has passed.`
                            : "Switched off manually - the class stays inactive regardless of its dates."}
                        </p>
                      </div>
                      <button
                        type="button"
                        role="switch"
                        id="class-active-switch"
                        aria-checked={Boolean(classForm.isActive)}
                        aria-labelledby="class-active-switch-label"
                        onClick={() =>
                          setClassForm((prev) => ({
                            ...prev,
                            isActive: !prev.isActive,
                          }))
                        }
                        className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-sunrise-red focus:ring-offset-2 ${
                          classForm.isActive
                            ? "bg-sunrise-red"
                            : "bg-slate-cloud/40"
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${
                            classForm.isActive
                              ? "translate-x-6"
                              : "translate-x-1"
                          }`}
                        />
                      </button>
                    </div>
                  </div>

                  {error && (
                    <div className="bg-sunrise-red/10 border border-sunrise-red/30 text-sunrise-red px-4 py-3 rounded-lg flex items-center">
                      <i className="fas fa-exclamation-triangle mr-2"></i>
                      {error}
                    </div>
                  )}
                </div>
              </form>
          </Modal>,
          document.body,
        )}

      {/* Delete Class Modal */}
      {classToDelete &&
        createPortal(
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="relative bg-white rounded-2xl shadow-brand-xl border border-pale-dawn max-w-md w-full">
              <div className="bg-gradient-to-r from-sunrise-red to-sunrise-red/90 text-white px-6 py-6 rounded-t-2xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mr-4">
                      <i className="fas fa-exclamation-triangle text-white text-xl"></i>
                    </div>
                    <div>
                      <h2 className="text-xl font-bold">Delete Class</h2>
                      <p className="text-white/80 text-sm">Permanent action</p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setClassToDelete(null);
                      setDeleteImpact(null);
                    }}
                    className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors duration-200"
                  >
                    <i className="fas fa-times text-white"></i>
                  </button>
                </div>
              </div>

              <div className="p-6">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-sunrise-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="fas fa-trash-alt text-sunrise-red text-2xl"></i>
                  </div>
                  <p className="text-slate-cloud text-lg mb-2">
                    Are you sure you want to delete the class
                  </p>
                  <p className="text-xl font-bold text-grey-dusk mb-4">
                    "{classToDelete?.name}"?
                  </p>
                  <div className="bg-sunrise-50 border border-sunrise-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <i className="fas fa-exclamation-triangle text-sunrise-red text-lg mr-3 mt-0.5"></i>
                      <div className="text-left">
                        <p className="text-sunrise-700 font-medium mb-1">
                          This action cannot be undone!
                        </p>
                        <p className="text-sunrise-600 text-sm">
                          This will hard-delete the class and all linked
                          semester data.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="rounded-lg border border-pale-dawn bg-ice-white p-4">
                    <p className="text-sm font-semibold text-grey-dusk mb-3">
                      What will be deleted
                    </p>
                    {deleteImpactLoading ? (
                      <p className="text-sm text-slate-cloud">
                        Loading delete preview...
                      </p>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-slate-cloud">
                        <p>Class record: 1</p>
                        <p>Semesters: {deleteImpact?.semesters ?? 0}</p>
                        <p>
                          Member links:{" "}
                          {deleteImpact?.memberLinks ??
                            classToDelete?.memberCount ??
                            0}
                        </p>
                        <p>
                          Subject assignments:{" "}
                          {deleteImpact?.subjectAssignments ??
                            classToDelete?.subjectCount ??
                            0}
                        </p>
                        <p>
                          Class subjects:{" "}
                          {deleteImpact?.classSpecificSubjects ?? 0}
                        </p>
                        <p>
                          Subject offerings:{" "}
                          {deleteImpact?.subjectOfferings ?? 0}
                        </p>
                        <p>Enrollments: {deleteImpact?.enrollments ?? 0}</p>
                        <p>Grade records: {deleteImpact?.gradeRecords ?? 0}</p>
                        <p>
                          Semester reviews: {deleteImpact?.semesterReviews ?? 0}
                        </p>
                        <p>Reports: {deleteImpact?.reportDocuments ?? 0}</p>
                      </div>
                    )}
                  </div>

                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                    <p className="text-sm font-semibold text-amber-900 mb-2">
                      Consequences
                    </p>
                    <div className="space-y-1 text-sm text-amber-800">
                      <p>Students will no longer be assigned to this class.</p>
                      <p>
                        All semesters created for this class will be removed.
                      </p>
                      <p>
                        Semester-based grades, reports and review data for this
                        class will be removed.
                      </p>
                      <p>This cannot be restored automatically afterwards.</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-pale-dawn px-6 py-4 flex justify-end space-x-3 border-t border-pale-dawn rounded-b-2xl">
                <button
                  type="button"
                  className="px-6 py-2 text-grey-dusk bg-ice-white border border-pale-dawn rounded-lg hover:bg-pale-dawn transition-colors duration-200 font-medium"
                  onClick={() => {
                    setClassToDelete(null);
                    setDeleteImpact(null);
                  }}
                >
                  <i className="fas fa-times mr-2"></i>
                  Cancel
                </button>
                <button
                  type="button"
                  className="px-6 py-2 bg-sunrise-red hover:bg-sunrise-red/90 text-white font-medium rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center shadow-lg hover:shadow-xl"
                  onClick={handleDeleteClass}
                  disabled={loading || deleteImpactLoading}
                >
                  <i
                    className={`fas ${loading || deleteImpactLoading ? "fa-spinner fa-spin" : "fa-trash-alt"} mr-2`}
                  ></i>
                  {loading
                    ? "Deleting..."
                    : deleteImpactLoading
                      ? "Preparing..."
                      : "Delete Class"}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* Delete School Modal */}
      {schoolToDelete &&
        createPortal(
          <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="delete-school-title"
              className="relative bg-white rounded-2xl shadow-brand-xl border border-pale-dawn max-w-lg w-full max-h-[90vh] flex flex-col"
            >
              <div className="bg-gradient-to-r from-sunrise-red to-sunrise-red/90 text-white px-6 py-6 rounded-t-2xl shrink-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mr-4">
                      <i className="fas fa-exclamation-triangle text-white text-xl"></i>
                    </div>
                    <div>
                      <h2
                        className="text-xl font-bold"
                        id="delete-school-title"
                      >
                        Delete School
                      </h2>
                      <p className="text-white/80 text-sm">
                        Permanent action - affects student data
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSchoolToDelete(null)}
                    className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors duration-200"
                  >
                    <i className="fas fa-times text-white"></i>
                  </button>
                </div>
              </div>

              <div className="p-6 overflow-y-auto">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-sunrise-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="fas fa-trash-alt text-sunrise-red text-2xl"></i>
                  </div>
                  <p className="text-slate-cloud text-lg mb-2">
                    Are you sure you want to delete the school
                  </p>
                  <p className="text-xl font-bold text-grey-dusk mb-4">
                    "{schoolToDelete?.name}"?
                  </p>
                  <div className="bg-sunrise-50 border border-sunrise-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <i className="fas fa-exclamation-triangle text-sunrise-red text-lg mr-3 mt-0.5"></i>
                      <div className="text-left">
                        <p className="text-sunrise-700 font-medium mb-1">
                          This action cannot be undone!
                        </p>
                        <p className="text-sunrise-600 text-sm">
                          Deleting a school removes every semester stored under
                          it. The student data hanging off those semesters goes
                          with them.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="rounded-lg border border-pale-dawn bg-ice-white p-4">
                    <p className="text-sm font-semibold text-grey-dusk mb-3">
                      What will be deleted
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-slate-cloud">
                      <p>School record: 1</p>
                      <p>All semesters of this school</p>
                      <p>
                        Classes still linked: {schoolClassesToDelete.length}
                      </p>
                      <p>
                        Students in those classes:{" "}
                        {schoolClassesToDelete.reduce(
                          (sum, classItem) =>
                            sum + (classItem.memberCount || 0),
                          0,
                        )}
                      </p>
                    </div>
                    {schoolClassesToDelete.length > 0 && (
                      <p className="mt-3 text-xs text-slate-cloud">
                        {schoolClassesToDelete
                          .map((classItem) => classItem.name)
                          .join(", ")}
                      </p>
                    )}
                  </div>

                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                    <p className="text-sm font-semibold text-amber-900 mb-2">
                      Consequences
                    </p>
                    <div className="space-y-1 text-sm text-amber-800">
                      <p>
                        Every semester belonging to this school is deleted, and
                        with it the grades, reports and semester reviews stored
                        against those semesters.
                      </p>
                      <p>
                        Apprentices lose the record of what they were graded on
                        in this school. Their accounts stay, their history does
                        not.
                      </p>
                      <p>
                        A class cannot exist without a school, so its classes
                        have to be deleted first - each one with its own
                        consequences.
                      </p>
                      <p>This cannot be restored automatically afterwards.</p>
                    </div>
                  </div>

                  {schoolClassesToDelete.length > 0 && (
                    <div className="rounded-lg border border-sunrise-200 bg-sunrise-50 p-4">
                      <div className="flex items-start">
                        <i className="fas fa-hand text-sunrise-red mr-3 mt-0.5"></i>
                        <div>
                          <p className="text-sm font-semibold text-sunrise-700 mb-1">
                            Delete its {schoolClassesToDelete.length}{" "}
                            {schoolClassesToDelete.length === 1
                              ? "class"
                              : "classes"}{" "}
                            first
                          </p>
                          <p className="text-sm text-sunrise-600">
                            A school that still has classes cannot be removed.
                            Delete each class through its own confirmation, so
                            you see exactly what each one takes with it.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-pale-dawn px-6 py-4 flex justify-end space-x-3 border-t border-pale-dawn rounded-b-2xl shrink-0">
                <button
                  type="button"
                  className="px-6 py-2 text-grey-dusk bg-ice-white border border-pale-dawn rounded-lg hover:bg-pale-dawn transition-colors duration-200 font-medium"
                  onClick={() => setSchoolToDelete(null)}
                >
                  <i className="fas fa-times mr-2"></i>
                  Cancel
                </button>
                <button
                  type="button"
                  className="px-6 py-2 bg-sunrise-red hover:bg-sunrise-red/90 text-white font-medium rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center shadow-lg hover:shadow-xl"
                  onClick={confirmDeleteSchool}
                  disabled={schoolClassesToDelete.length > 0}
                  title={
                    schoolClassesToDelete.length > 0
                      ? "Delete the classes of this school first"
                      : undefined
                  }
                >
                  <i className="fas fa-trash-alt mr-2"></i>
                  Delete School
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default SchoolClassManagement;
